import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTemplateManifest, useAssignTemplate, type TemplateEntry } from '../../hooks/useProjects.js';
import { Button } from '../ui/Button.js';
import { useToast } from '../ui/Toast.js';

interface Props {
  projectId: number;
  selectedSlug?: string;
  onAssigned?: () => void;
}

interface CardProps {
  template: TemplateEntry;
  isAssigning: boolean;
  isTargeted: boolean;
  isSelected: boolean;
  onShowcase: () => void;
  onSelect: () => void;
}

function TemplateCard({ template, isAssigning, isTargeted, isSelected, onShowcase, onSelect }: CardProps) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const dimmed = isAssigning && !isTargeted;
  const showActive = isSelected && !isAssigning;
  const showLoading = isTargeted && isAssigning;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--evo-bg-2)',
        border: `1px solid ${isSelected || (isTargeted && isAssigning) ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
        borderRadius: 'var(--evo-r)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transform: hovered && !isAssigning ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hovered && !isAssigning
          ? '0 8px 24px rgba(0,0,0,0.45)'
          : isSelected ? '0 0 0 1px var(--evo-cyan)' : '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'transform 180ms ease-out, box-shadow 180ms ease-out, opacity 160ms ease-out, border-color 160ms ease-out',
        opacity: dimmed ? 0.4 : 1,
        pointerEvents: dimmed ? 'none' : 'auto',
      }}
    >
      {/* Thumbnail */}
      <div style={{ aspectRatio: '16/10', overflow: 'hidden', flexShrink: 0, background: 'var(--evo-bg)', position: 'relative' }}>
        <img
          src={`/templates/${template.slug}/thumbnail.png`}
          alt={template.name}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        {showActive && (
          <div style={{
            position: 'absolute',
            top: 'var(--evo-space-2)',
            right: 'var(--evo-space-2)',
            background: 'var(--evo-cyan)',
            color: 'var(--evo-bg)',
            fontSize: 'var(--evo-fs-caption)',
            fontFamily: 'var(--evo-font)',
            fontWeight: 'var(--evo-fw-semibold)',
            padding: '2px 8px',
            borderRadius: 'var(--evo-r-pill)',
          }}>
            ✓ {t('projects.gallery.active')}
          </div>
        )}
      </div>

      {/* Info + actions */}
      <div style={{
        padding: 'var(--evo-space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--evo-space-2)',
        flex: 1,
      }}>
        <div style={{
          fontSize: 'var(--evo-fs-body)',
          fontWeight: 'var(--evo-fw-semibold)',
          color: 'var(--evo-fg-1)',
          fontFamily: 'var(--evo-font)',
          lineHeight: 1.3,
        }}>
          {template.name}
        </div>
        <div style={{
          fontSize: 'var(--evo-fs-label)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {template.personality}
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-2)',
          marginTop: 'var(--evo-space-1)',
        }}>
          <Button variant="link" tone="muted" size="sm" onClick={onShowcase}>
            {t('projects.gallery.showcase')}
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            size="sm"
            variant={showActive ? 'solid' : 'outline'}
            tone="cyan"
            onClick={onSelect}
            disabled={isAssigning || showActive}
          >
            {showLoading ? '…' : showActive ? t('projects.gallery.active') : t('projects.gallery.select')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{
      background: 'var(--evo-bg-2)',
      border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r)',
      overflow: 'hidden',
    }}>
      <div style={{ aspectRatio: '16/10', background: 'var(--evo-bg-3)' }} />
      <div style={{ padding: 'var(--evo-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
        <div style={{ height: '14px', width: '45%', background: 'var(--evo-bg-3)', borderRadius: 'var(--evo-r-sm)' }} />
        <div style={{ height: '12px', width: '80%', background: 'var(--evo-bg-3)', borderRadius: 'var(--evo-r-sm)' }} />
      </div>
    </div>
  );
}

export function TemplateGallery({ projectId, selectedSlug, onAssigned }: Props) {
  const { t } = useTranslation();
  const { data: manifest, isLoading, isError } = useTemplateManifest();
  const assignTemplate = useAssignTemplate(projectId);
  const toast = useToast();
  const [targetSlug, setTargetSlug] = useState<string | null>(null);

  function handleSelect(slug: string) {
    setTargetSlug(slug);
    assignTemplate.mutate(slug, {
      onSuccess: () => {
        setTargetSlug(null);
        toast.success(t('projects.gallery.assignedLabel'));
        onAssigned?.();
      },
      onError: (err) => {
        setTargetSlug(null);
        const msg = err instanceof Error ? err.message : '';
        toast.error(`${t('projects.gallery.assignError')}${msg ? ` ${msg}` : ''}`);
      },
    });
  }

  function handleShowcase(template: TemplateEntry) {
    window.open(`/templates/${template.slug}/${template.showcase}`, '_blank', 'noopener,noreferrer');
  }

  if (isLoading) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'var(--evo-space-4)',
      }}>
        {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  if (isError || !manifest) {
    return (
      <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)' }}>
        {t('projects.gallery.loadError')}
      </p>
    );
  }

  const isAssigning = assignTemplate.isPending;

  return (
    <div>
      <div style={{ marginBottom: 'var(--evo-space-5)' }}>
        <div style={{
          fontSize: 'var(--evo-fs-h3)',
          fontWeight: 'var(--evo-fw-semibold)',
          color: 'var(--evo-fg-1)',
          fontFamily: 'var(--evo-font)',
          marginBottom: 'var(--evo-space-1)',
        }}>
          {t('projects.gallery.heading')}
        </div>
        <div style={{
          fontSize: 'var(--evo-fs-body)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
        }}>
          {t('projects.gallery.subtitle')}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'var(--evo-space-4)',
      }}>
        {manifest.templates.map(template => (
          <TemplateCard
            key={template.slug}
            template={template}
            isAssigning={isAssigning}
            isTargeted={targetSlug === template.slug}
            isSelected={selectedSlug === template.slug}
            onShowcase={() => handleShowcase(template)}
            onSelect={() => handleSelect(template.slug)}
          />
        ))}
      </div>
    </div>
  );
}
